import React from 'react';
import { InfoBox } from '../../components/InfoBox';
interface CheckoutOverviewPageProps {
  onNavigate?: (pageId: string) => void;
}
export function CheckoutOverviewPage({
  onNavigate
}: CheckoutOverviewPageProps) {
  return <div className="max-w-4xl mx-auto px-8 py-12" data-id="element-1303">
      <div className="text-sm text-gray-500 mb-4" data-id="element-1304">
        <button onClick={() => onNavigate?.('overview')} className="hover:underline text-orange-600" data-id="element-1305">
          Overview
        </button>{' '}
        &gt; &lt;&lt;checkout&gt;&gt;
      </div>

      <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-8" data-id="element-1306">
        &lt;&lt;checkout&gt;&gt;
      </h1>

      <p className="text-gray-700 mb-4 leading-relaxed" data-id="element-1307">
        The &lt;&lt;checkout&gt;&gt; integration method is ideal when you want a
        solution that is both secure and fast to deploy. In this integration,
        you create a checkout session where the &lt;&lt;paymentGateway&gt;&gt;
        hosts a payment page UI that gathers payment information from the
        customer, so you need not handle sensitive data at all.
      </p>

      <p className="text-gray-700 mb-4 leading-relaxed" data-id="element-1308">
        For initial transactions that require payer participation (to provide
        their payment details), you use the checkout session to display a
        &lt;&lt;hostedPaymentPage&gt;&gt; for the payer. For merchant-initiated
        and subsequent transactions that do not require payer participation, you
        simply send an API request from your server.
      </p>

      <p className="text-gray-700 mb-8 leading-relaxed" data-id="element-1309">
        &lt;&lt;checkout&gt;&gt; does have some limitations, mainly that you
        cannot customize the payment flow and payment page UI beyond adding your
        merchant-specific name and logo.
      </p>

      <h2 className="text-2xl font-bold text-gray-900 mt-10 mb-6" data-id="element-1310">
        Supported payment methods and transactions
      </h2>

      <div className="overflow-x-auto mb-8" data-id="element-1311">
        <table className="w-full text-sm border border-gray-200 rounded-lg overflow-hidden" data-id="element-1312">
          <thead data-id="element-1313">
            <tr className="bg-gray-50" data-id="element-1314">
              <th className="text-left px-6 py-4 font-semibold text-gray-900 border-b border-r border-gray-200 w-1/2" data-id="element-1315">
                Payment methods
              </th>
              <th className="text-left px-6 py-4 font-semibold text-gray-900 border-b border-gray-200 w-1/2" data-id="element-1316">
                Transactions
              </th>
            </tr>
          </thead>
          <tbody data-id="element-1317">
            <tr data-id="element-1318">
              <td className="px-6 py-4 align-top border-r border-gray-200" data-id="element-1319">
                <p className="mb-2 text-gray-700" data-id="element-1320">All except:</p>
                <ul className="list-disc list-inside space-y-1.5 text-gray-700 ml-2" data-id="element-1321">
                  <li data-id="element-1322">Aggregated Transit Fare</li>
                  <li data-id="element-1323">Cardholder Present Payments</li>
                  <li data-id="element-1324">Chase Pay</li>
                  <li data-id="element-1325">Contactless Mobile Pay</li>
                  <li data-id="element-1326">Samsung Pay</li>
                  <li data-id="element-1327">SEPA</li>
                  <li data-id="element-1328">WeChat Pay</li>
                </ul>
              </td>
              <td className="px-6 py-4 align-top" data-id="element-1329">
                <p className="mb-2 text-gray-700" data-id="element-1330">Transactions:</p>
                <p className="font-bold text-gray-900 mb-2 mt-3" data-id="element-1331">
                  With payer participation:
                </p>
                <ul className="list-disc list-inside space-y-1.5 text-gray-700 ml-2 mb-4" data-id="element-1332">
                  <li data-id="element-1333">AUTHORIZE</li>
                  <li data-id="element-1334">PAY</li>
                  <li data-id="element-1335">VERIFY</li>
                </ul>
                <p className="font-bold text-gray-900 mb-2 mt-3" data-id="element-1336">
                  Without payer participation:
                </p>
                <ul className="list-disc list-inside space-y-1.5 text-gray-700 ml-2" data-id="element-1337">
                  <li data-id="element-1338">UPDATE AUTHORIZATION</li>
                  <li data-id="element-1339">CAPTURE</li>
                  <li data-id="element-1340">REFUND</li>
                  <li data-id="element-1341">RETRIEVE ORDER</li>
                  <li data-id="element-1342">VOID</li>
                  <li data-id="element-1343">REFERRAL</li>
                  <li data-id="element-1344">CHARGEBACK</li>
                </ul>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <InfoBox type="info" data-id="element-1345">
        Only the VERIFY transaction is supported as a transaction with payer
        participation with the gift card and ACH payment methods.
      </InfoBox>

      <h2 className="text-2xl font-bold text-gray-900 mt-10 mb-4" data-id="element-1346">
        Key benefits
      </h2>
      <ul className="list-disc list-inside space-y-2 text-gray-700 mb-8 ml-4" data-id="element-1347">
        <li data-id="element-1348">
          <strong data-id="element-1349">Reduced PCI Compliance:</strong> Since the gateway hosts the
          payment page, sensitive card data never touches your servers,
          significantly reducing your PCI DSS compliance scope (qualifies for
          SAQ A).
        </li>
        <li data-id="element-1350">
          <strong data-id="element-1351">Fast Integration:</strong> Easy to implement with minimal
          coding required on your end.
        </li>
        <li data-id="element-1352">
          <strong data-id="element-1353">Always Up-to-Date:</strong> Benefit automatically from new
          features, payment methods, and security updates without changing your
          integration.
        </li>
        <li data-id="element-1354">
          <strong data-id="element-1355">Mobile Optimized:</strong> The hosted payment page is fully
          responsive and optimized for all devices.
        </li>
      </ul>

      <h2 className="text-2xl font-bold text-gray-900 mt-10 mb-4" data-id="element-1356">
        Information flow
      </h2>
      <ol className="list-decimal list-inside space-y-3 text-gray-700 mb-8 ml-4" data-id="element-1357">
        <li data-id="element-1358">Your server initiates a checkout session via the API.</li>
        <li data-id="element-1359">
          You receive a session ID and redirect the customer to the hosted
          payment page.
        </li>
        <li data-id="element-1360">
          The customer enters their payment details securely on the
          gateway-hosted page.
        </li>
        <li data-id="element-1361">
          The gateway processes the payment and redirects the customer back to
          your site.
        </li>
        <li data-id="element-1362">Your server verifies the transaction result.</li>
      </ol>
    </div>;
}